import { useCallback, useMemo, useState } from "react"
import { GetApp } from "../../App"
import { GetDay } from "../../App/Context"
import { GetShipUsers } from "../ShipView"
import { Block, BlockField, BlockRow } from "../../Components/Block"
import { MultiSelect } from "../../Components/MultiSelect"
import { Overlay } from "../../Components/Overlay"
import { Select, SelectOption } from "../../Components/Select"
import { ShipUserSelectData } from "../../Components/ShipUserSelect"
import { AlertApi, Day, IAlertLoudness, IAlertMessageRequest, IAlertStatus, IDay, IShipUser } from "../../Types"
import { logger } from "../../App/Logger"

// Pure logic for the "send alert" dialog, kept separate from the React tree so it
// can be unit-tested without mounting the app contexts.
export const SendAlertDialogData = {
  // Options for the recipients multi-select: the ship crew, optionally restricted
  // to the current day's crew ids, keyed by the (string) user id.
  userOptions(shipUsers: IShipUser[] | undefined, crewIds: number[] | undefined): SelectOption[] {
    return (shipUsers || [])
      .filter((u) => crewIds == undefined || crewIds.includes(u.id))
      .map((u) => ({ title: ShipUserSelectData.userLabel(u), value: String(u.id) }));
  },

  // Options for the watch-group multi-select, resolved from the day's persisted
  // watch data. Unnamed groups are skipped (they cannot be labelled).
  groupOptions(day: IDay | undefined): SelectOption[] {
    if (!day) return [];
    const groups = Day.parse(day).dataParsed.watch?.groups || [];
    return groups
      .filter((g) => g.name.trim().length > 0)
      .map((g) => ({ title: g.name, value: g.id }));
  },

  // A message alert may be sent only with a non-empty body and at least one
  // recipient (a user or a watch group).
  canSend(message: string, users: string[], groups: string[]): boolean {
    return message.trim().length > 0 && (users.length > 0 || groups.length > 0);
  },

  buildRequest(
    users: string[],
    groups: string[],
    message: string,
    severity: IAlertStatus,
    loudness: IAlertLoudness,
  ): IAlertMessageRequest {
    return {
      users: users.map((u) => parseInt(u, 10)),
      groups,
      message: message.trim(),
      severity,
      loudness,
    };
  },
};

interface SendAlertDialogProps {
  onClose: () => void;
  initUsers?: string[];
  initGroups?: string[];
  initMessage?: string;
  initSeverity?: IAlertStatus;
  initLoudness?: IAlertLoudness;
  initBusy?: boolean;
  initFailed?: boolean;
}

// The ad-hoc "message" alert composer: pick recipients (crew users and/or watch
// groups), type a message, choose severity + loudness, and fan it out over the
// same LAN pub/sub + native delivery the sensor alerts use. Reached from the
// LogButton dropdown on the editable current-day log. The `init*` props seed the
// otherwise-internal state (all optional, defaulting to today's empty/unset
// values) so the catalog can render each state declaratively.
export function SendAlertDialog({
  onClose,
  initUsers,
  initGroups,
  initMessage,
  initSeverity,
  initLoudness,
  initBusy,
  initFailed,
}: SendAlertDialogProps) {
  const app = GetApp();
  const t = app.getT("SendAlertDialog");
  const { day } = GetDay();
  const shipUsers = GetShipUsers();

  const userOptions = useMemo(() => SendAlertDialogData.userOptions(shipUsers, day?.crew), [shipUsers, day]);
  const groupOptions = useMemo(() => SendAlertDialogData.groupOptions(day), [day]);

  const [users, setUsers] = useState<string[]>(initUsers ?? []);
  const [groups, setGroups] = useState<string[]>(initGroups ?? []);
  const [message, setMessage] = useState(initMessage ?? "");
  const [severity, setSeverity] = useState<IAlertStatus>(initSeverity ?? IAlertStatus.warning);
  const [loudness, setLoudness] = useState<IAlertLoudness>(initLoudness ?? IAlertLoudness.audible);
  const [busy, setBusy] = useState(initBusy ?? false);
  const [failed, setFailed] = useState(initFailed ?? false);

  const severityOptions: SelectOption[] = useMemo(() => [
    { title: t("severity_warning"), value: IAlertStatus.warning },
    { title: t("severity_alarm"), value: IAlertStatus.alarm },
  ], [app.language]);
  const loudnessOptions: SelectOption[] = useMemo(() => [
    { title: t("loudness_audible"), value: IAlertLoudness.audible },
    { title: t("loudness_notificationOnly"), value: IAlertLoudness.notificationOnly },
  ], [app.language]);

  const canSend = useMemo(() => SendAlertDialogData.canSend(message, users, groups), [message, users, groups]);

  const send = useCallback(async () => {
    if (!canSend || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const body = SendAlertDialogData.buildRequest(users, groups, message, severity, loudness);
      await new AlertApi(app.apiClient).alert.message({ params: {}, body }).call();
      onClose();
    } catch (error) {
      logger.error("SendAlertDialog send", error);
      setFailed(true);
      setBusy(false);
    }
  }, [canSend, busy, users, groups, message, severity, loudness, app, onClose]);

  const userValues = useMemo(() =>
    users.map((id) => ({ value: id, title: userOptions.find((o) => o.value == id)?.title || id })), [users, userOptions]);
  const groupValues = useMemo(() =>
    groups.map((id) => ({ value: id, title: groupOptions.find((o) => o.value == id)?.title || id })), [groups, groupOptions]);

  return (
    <Overlay className="SendAlertDialog" closeCallback={onClose} title={t("title")}>
      <div className="Overlay-body">
        <Block>
          <BlockRow>
            <BlockField label={t("users")}>
              <MultiSelect button={<div>+</div>} name="users" onSelect={setUsers} options={userOptions} values={userValues} />
            </BlockField>
          </BlockRow>
          {(groupOptions.length > 0 || groups.length > 0) && (
            <BlockRow>
              <BlockField label={t("groups")}>
                <MultiSelect button={<div>+</div>} name="groups" onSelect={setGroups} options={groupOptions} values={groupValues} />
              </BlockField>
            </BlockRow>
          )}
          <BlockRow>
            <BlockField label={t("message")}>
              <textarea className="SendAlertDialog-message" name="message" value={message} onChange={(e) => setMessage(e.target.value)} />
            </BlockField>
          </BlockRow>
          <BlockRow>
            <BlockField label={t("severity")}>
              <Select button={severityOptions.find((o) => o.value == severity)?.title || ""} name="severity" value={severity} onSelect={(v) => setSeverity(v)} options={severityOptions} />
            </BlockField>
            <BlockField label={t("loudness")}>
              <Select button={loudnessOptions.find((o) => o.value == loudness)?.title || ""} name="loudness" value={loudness} onSelect={(v) => setLoudness(v)} options={loudnessOptions} />
            </BlockField>
          </BlockRow>
          {failed && <BlockRow><BlockField><span className="SendAlertDialog-error">{t("error")}</span></BlockField></BlockRow>}
          <BlockRow actions={true}>
            <BlockField><button className="button default" onClick={onClose}>{t("cancel")}</button></BlockField>
            <BlockField><button className="button primary" disabled={!canSend || busy} onClick={send}>{t("send")}</button></BlockField>
          </BlockRow>
        </Block>
      </div>
    </Overlay>
  );
}
